import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { mapIdFromPrettyPath } from '../../utils/prettyRoutes';
import { MapImageView } from '../../components/world/MapImageView';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { ACCESS_PENDING_LABEL, accessLabel, mapPath, monsterName, monsterPath, prettyAccessName } from '../../utils/worldFormat';
import { thirdEvolution } from '../../data/guide/evolutions';
import type { WorldData, WorldMap } from '../../types/world';
type Presence = 'todo' | 'permanente' | 'temporal';
export function MapDetail() {
  const {
    mapId,
    mapSlug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-3395" data-mp="1v72r2dz5ohtd">
      {w => {
      const id = mapSlug ? mapIdFromPrettyPath(pathname) : Number(mapId);
      const m = id === undefined ? undefined : w.maps.get(id);
      if (!m) return <NotFound data-id="element-3396" data-mp="13goj0ydtpzpm" />;
      return <>
            <CanonicalPath to={mapPath(m.id)} data-id="element-3397" data-mp="13i9c0jkhpald" />
            <MapView w={w} m={m} data-id="element-3398" data-mp="17gzzdyz5ldva" />
          </>;
    }}
    </WorldGate>;
}
function NotFound() {
  return <div className="rounded-2xl border border-white/10 p-6" data-id="element-3399" data-mp="1cg357lmyjo4b">
      <p className="text-white" data-id="element-3400" data-mp="1hfo5q0hs04j5">No encontramos ese mapa en los archivos.</p>
      <Link to="/guia/mapas" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-3401" data-mp="1hdl4ekb6aoz4">Volver a Mapas y spots</Link>
    </div>;
}
function MapView({
  w,
  m
}: {
  w: WorldData;
  m: WorldMap;
}) {
  const [presence, setPresence] = useState<Presence>('todo');
  useEffect(() => {
    document.title = `${m.name} · Mapas · Guía MU Vanguard`;
  }, [m.name]);
  const spawns = w.idx.spawnsByMap.get(m.id) ?? [];
  const spots = useMemo(() => spawns.filter(s => s.kind === 'spot').sort((a, b) => (w.monsters.get(a.monsterId)?.level ?? 0) - (w.monsters.get(b.monsterId)?.level ?? 0) || a.monsterId - b.monsterId), [spawns, w]);
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const locations = w.idx.locationsByMap.get(m.id) ?? [];
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const eventIds = Array.from(new Set([...spawns, ...locations].map(x => x.eventId).filter((v): v is string => !!v)));
  const showPermanent = presence !== 'temporal';
  const showTemporary = presence !== 'permanente';
  const filters: {
    id: Presence;
    label: string;
  }[] = [{
    id: 'todo',
    label: 'Todo'
  }, {
    id: 'permanente',
    label: 'Permanente'
  }, {
    id: 'temporal',
    label: 'Eventos e invasiones'
  }];
  return <div data-id="element-3402" data-mp="3d4ypvx0pgtyk">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: m.name
    }]} eyebrow="Mapa" title={m.name} data-id="element-3403" data-mp="3d4p1gdvb6uuz">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-3404" data-mp="2ydzl6x2zqpk9">
          <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-3405" data-mp="33fxb2a5wcim6">
            {accessLabel(m)}
          </span>
          <span className="text-sm text-gray-400" data-id="element-3406" data-mp="3w42wz08osdnl">{spots.length} spots · {new Set(locations.map(l => l.monsterId)).size} monstruos documentados</span>
          <CopyLinkButton label="Copiar enlace del mapa" data-id="element-3407" data-mp="3i64i7tqgef7k" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-3408" data-mp="3m7q424kyu3tw">
        <WorldSection id="accesos" title="Accesos y requisitos" data-id="element-3409" data-mp="26p4w5fup8wlz">
          {m.moveEntries.length ? <ul className="divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-3410" data-mp="2aqzt685lknty">
              {m.moveEntries.map(e => <li key={`${e.name}-${e.gateId ?? ''}`} className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-3411" data-mp="2qyeem1ija5sx">
                  <span className="text-white" data-id="element-3412" data-mp="2m5l755vokjd5">{prettyAccessName(e.name)}</span>
                  <span className="text-sm tabular-nums text-gray-300" data-id="element-3413" data-mp="2h8l12uytzgm1">{e.minLevel !== undefined ? `Nivel ${e.minLevel}` : 'Nivel no documentado'}</span>
                </li>)}
            </ul> : <p className="text-gray-400" data-id="element-3414" data-mp="39ue9pl6t5pgq">
              {ACCESS_PENDING_LABEL}. Sin /move documentado: falta el mecanismo de acceso (portal, NPC o misión) y su condición real en Vanguard.
            </p>}
          {[thirdEvolution.stage2.map, thirdEvolution.stage3.map].some(x => m.name.toLowerCase().split(' ').includes(x.toLowerCase())) && <p className="mt-3 text-sm text-gray-300" data-id="element-3415" data-mp="1ok748ijhaqc2">
              Relacionado:{' '}
              <Link to="/guia/evoluciones#tercera" className="font-semibold text-[#D4AF37] underline-offset-4 hover:underline" data-id="element-3416" data-mp="1khat1cw9pwi5">
                misión de la tercera evolución
              </Link>{' '}
              que se hace en este mapa.
            </p>}
          {m.notes.length > 0 && <ul className="mt-3 space-y-1.5 text-sm text-gray-400" data-id="element-3417" data-mp="8zy2l6frgrjq">
              {m.notes.map(n => <li key={n} data-id="element-3418" data-mp="drg724gzta9g">{n}</li>)}
            </ul>}
          <p className="mt-3 text-sm text-gray-500" data-id="element-3419" data-mp="dszjfjzj315y">Cada acceso tiene su propio requisito. Los spots no se asignan a una subzona concreta.</p>
        </WorldSection>

        <div role="group" aria-label="Filtrar presencia" className="flex flex-wrap gap-1.5" data-id="element-3420" data-mp="nnriytco7y3d">
          {filters.map(f => <button key={f.id} type="button" aria-pressed={presence === f.id} onClick={() => setPresence(f.id)} className={`h-11 rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${presence === f.id ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-3421" data-mp="rol6hlm7bavq">
              {f.label}
            </button>)}
        </div>

        <WorldSection id="imagen" title="Mapa" data-id="element-3422" data-mp="ovp6j891j44">
          <MapImageView w={w} mapId={m.id} spots={showPermanent ? spots : invasionSpawns} data-id="element-3423" data-mp="enbuq6udl3ko" />
        </WorldSection>

        {showPermanent && <WorldSection id="spots" title="Spots configurados" count={spots.length} data-id="element-3424" data-mp="9n7gyokuqmy5">
            {spots.length ? <ul className="grid gap-2.5" data-id="element-3425" data-mp="ojyfywqm5wn8">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="monster" data-id="element-3426" data-mp="jmog0n5lm2l5" />)}</ul> : <p className="text-gray-400" data-id="element-3427" data-mp="xdtkc2jwvj1t">No hay spots configurados en spawns.csv para este mapa.</p>}
          </WorldSection>}

        {showTemporary && <WorldSection id="invasiones" title="Apariciones de invasión" count={invasionSpawns.length} data-id="element-3428" data-mp="tecdp5sr0fv2">
            {invasionSpawns.length ? <ul className="grid gap-2.5" data-id="element-3429" data-mp="12dpe78yw9u3n">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="monster" data-id="element-3430" data-mp="12ati7q6lxfhu" />)}</ul> : <p className="text-gray-400" data-id="element-3431" data-mp="1lzhg0nhqrzeb">No hay apariciones de invasión configuradas en este mapa.</p>}
          </WorldSection>}

        <WorldSection id="monstruos" title="Monstruos presentes" data-id="element-3432" data-mp="1c11t6wpqzcam">
          {(showPermanent ? permanent : []).length + (showTemporary ? temporary : []).length === 0 ? <p className="text-gray-400" data-id="element-3433" data-mp="13888gjrj7kpd">Sin presencia documentada en monster_locations.csv.</p> : <ul className="grid gap-2 sm:grid-cols-2" data-id="element-3434" data-mp="17yomo636facs">
              {[...(showPermanent ? permanent : []), ...(showTemporary ? temporary : [])].map(l => <li key={l.id} data-id="element-3435" data-mp="tbvf5mzwf8v5">
                  <Link to={monsterPath(l.monsterId)} className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#0A1628] px-4 py-3 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-3436" data-mp="y8vme18hihnu">
                    <span className="flex flex-wrap items-baseline gap-x-2" data-id="element-3437" data-mp="oapkhjmohbiz">
                      <span className="font-semibold text-white" data-id="element-3438" data-mp="ocs7rvwg4qk0">{monsterName(w, l.monsterId)}</span>
                      {w.monsters.get(l.monsterId)?.level !== undefined && <span className="text-xs text-gray-500" data-id="element-3439" data-mp="zsezx6yudop5">Nv. monstruo {w.monsters.get(l.monsterId)!.level}</span>}
                    </span>
                    <span className={`mt-0.5 text-sm ${l.kind === 'ordinary' ? 'text-gray-400' : 'text-[#D4AF37]/90'}`} data-id="element-3440" data-mp="uu3ur9jhs2d9">{l.condition ?? 'Condición no documentada'}</span>
                  </Link>
                </li>)}
            </ul>}
        </WorldSection>

        {eventIds.length > 0 && showTemporary && <WorldSection id="eventos" title="Eventos e invasiones asociados" data-id="element-3441" data-mp="c19k2rswiweu">
            <div className="flex flex-wrap gap-2" data-id="element-3442" data-mp="72pwyo2knekw">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-3443" data-mp="749e0gw1xtnq" />)}</div>
          </WorldSection>}

        <Disclosure title="Configuración técnica del mapa" data-id="element-3444" data-mp="233oerxoumif">
          <StatList rows={[{
          label: 'experience_rate',
          value: m.experienceRate
        }, {
          label: 'item_drop_rate',
          value: m.itemDropRate
        }, {
          label: 'excellent_rate_per_million',
          value: m.excellentRatePerMillion
        }, {
          label: 'ancient_rate_per_million',
          value: m.ancientRatePerMillion
        }, {
          label: 'socket_items_allowed',
          value: m.socketItemsAllowed === undefined ? undefined : m.socketItemsAllowed ? 'Sí' : 'No'
        }, {
          label: 'ID del mapa',
          value: m.id
        }]} data-id="element-3445" data-mp="3u6w5josvdor6" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-3446" data-mp="1pzjjka3r4j2k">Valores tal como figuran en maps.csv. No son probabilidades finales por monstruo.</p>
        </Disclosure>
      </div>
    </div>;
}